import { faAdd, faCog, faSync } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation } from '@tanstack/react-query'
import { Outlet, useMatchRoute, useNavigate } from '@tanstack/react-router'
import { observer } from 'mobx-react-lite'
import { useCallback, useRef, useState } from 'react'
import { StaticAlert } from '~/Components/Alert'
import { Button, ButtonGroup } from '~/Components/Button'
import { Callout } from '~/Components/Callout'
import { useTwoPanelMode } from '~/Hooks/useLayoutMode'
import { useShowSecondaryPanel } from '~/Hooks/useShowSecondaryPanel'
import { ContextHelpButton } from '~/Layout/PanelIcons'
import { SplitPanels } from '~/Layout/SplitPanels.js'
import { MyErrorBoundary } from '~/Resources/Error'
import { trpc } from '~/Resources/TRPC'
import { AddEmulatorModal, type AddEmulatorModalRef } from './AddEmulatorModal'
import { AddSurfaceGroupModal, type AddSurfaceGroupModalRef } from './AddGroupModal'
import { KnownSurfacesTable } from './KnownSurfacesTable'
import { UdevRulesAlert } from './UdevRulesAlert'
import { UsbUnavailableNote } from './UsbUnavailableNote'

export const MainSurfacesPage = observer(function MainSurfacesPage(): React.JSX.Element {
	const twoPanelMode = useTwoPanelMode()

	const navigate = useNavigate()
	const matchRoute = useMatchRoute()

	const routeMatch = matchRoute({ to: '/surfaces/$itemId' })
	const selectedSurfaceId = routeMatch ? routeMatch.itemId : null

	const addGroupModalRef = useRef<AddSurfaceGroupModalRef>(null)
	const addEmulatorModalRef = useRef<AddEmulatorModalRef>(null)

	const [scanError, setScanError] = useState<string | null>(null)

	const rescanUsbMutation = useMutation(trpc.surfaces.rescanUsb.mutationOptions())
	const rescanUsbMutationAsync = rescanUsbMutation.mutateAsync

	const refreshUSB = useCallback(() => {
		setScanError(null)

		rescanUsbMutationAsync() // TODO: 30s timeout?
			.then((errorMsg) => {
				setScanError(errorMsg || null)
			})
			.catch((err) => {
				console.error('Refresh USB failed', err)
				setScanError('Rescanning USB devices failed! Please try again.')
			})
	}, [rescanUsbMutationAsync])

	const addEmulator = useCallback(() => {
		addEmulatorModalRef.current?.show()
	}, [])
	const addGroup = useCallback(() => {
		addGroupModalRef.current?.show()
	}, [])

	// Handle action when the user clicks the "Show Settings" button
	const handleShowSettings = useCallback(() => {
		void navigate({ to: '/surfaces/integrations' })
	}, [navigate])

	// Handle the various cases in which we want to show the secondary panel in one-panel mode
	// 1. if one of the integration subpanels are currently visible or user clicked "Show Settings"
	const showSettings = useShowSecondaryPanel({
		baseRoute: '/surfaces',
		secondaryRoute: '/surfaces/integrations',
	})

	// 2. if editing known-surfaces (aka configured surfaces)
	const selectKnownSurface = useCallback(
		(itemId: string | null) => {
			if (itemId === null || selectedSurfaceId === itemId) {
				void navigate({ to: '/surfaces' })
			} else {
				void navigate({
					to: '/surfaces/$itemId',
					params: {
						itemId: itemId,
					},
				})
			}
		},
		[navigate, selectedSurfaceId]
	)

	return (
		<SplitPanels.Root
			showing={selectedSurfaceId || showSettings ? 'secondary' : 'primary'}
			className="surfaces-page"
			resize={{ storageKey: 'surfaces' }}
		>
			<SplitPanels.Primary className="flex-column-layout">
				<div className="fixed-header">
					<h4 className="button-inline">
						Surfaces
						<ContextHelpButton action="/user-guide/config/surfaces">
							Use the table, below to configure currently-known surfaces and groups.
							<br />
							To configure a surface integration or global setting,
							{twoPanelMode
								? ' use the panel to the right.'
								: '	click the "Show Settings" button on the right, just above the table.'}
							<br />
							Click this icon for more help.
						</ContextHelpButton>
					</h4>

					<p className="mb-2">
						Click on any item below to edit the configuration of a currently-known surface or group.
						<br />
						If your streamdeck is missing from this list, you might need to close the Elgato Streamdeck application and
						click the Rescan button below.
					</p>

					{scanError && (
						<StaticAlert color="warning" role="alert">
							{scanError}
						</StaticAlert>
					)}

					<UsbUnavailableNote />

					<UdevRulesAlert />

					<ButtonGroup>
						<Button color="warning" size="sm" onClick={refreshUSB}>
							<FontAwesomeIcon icon={faSync} spin={rescanUsbMutation.isPending} />
							{rescanUsbMutation.isPending ? ' Checking for new surfaces...' : ' Rescan USB'}
						</Button>
						<Button color="primary" size="sm" onClick={addEmulator}>
							<FontAwesomeIcon icon={faAdd} /> Add Emulator
						</Button>
						<Button color="secondary" size="sm" onClick={addGroup}>
							<FontAwesomeIcon icon={faAdd} /> Add Group
						</Button>
					</ButtonGroup>

					<AddSurfaceGroupModal ref={addGroupModalRef} />
					<AddEmulatorModal ref={addEmulatorModalRef} />

					{!twoPanelMode && (
						<Button color="info" className="float-right" size="sm" onClick={handleShowSettings}>
							<FontAwesomeIcon icon={faCog} /> Show Settings
						</Button>
					)}
				</div>

				<KnownSurfacesTable selectedItemId={selectedSurfaceId} selectItem={selectKnownSurface} />

				<div className="fixed-header">
					<Callout color="info">
						Did you know, you can connect a Streamdeck from another computer or Raspberry Pi with{' '}
						<a target="_blank" rel="noreferrer" href="https://l.companion.free/q/YH8dZkH1Q">
							Companion Satellite
						</a>
						?
					</Callout>
				</div>
			</SplitPanels.Primary>

			<SplitPanels.Secondary>
				<div className="secondary-panel-simple">
					<MyErrorBoundary>
						<Outlet />
					</MyErrorBoundary>
				</div>
			</SplitPanels.Secondary>
		</SplitPanels.Root>
	)
})
